/**
 * dimensionRegistry — Phase 5.3.d sub-machine collapse.
 *
 * Wraps the 21-case `advanceDimensionWithPoint` switch in
 * `apps/web/src/app/scene/draftStateMachine.dimensions.ts` as
 * `Partial<Record<DraftStep, AdvanceWithPointFn>>` so each
 * dimension step participates in the unified `ADVANCE_WITH_POINT`
 * registry alongside LINE / CIRCLE / etc.
 *
 * The wrapper delegates verbatim to `advanceDimensionWithPoint`
 * (no behavioural change). PR 5.3.d ships the wrapper; future PRs
 * (5.3.f / 5.3.h cleanup) may inline individual cases as their own
 * registry entries once the migration pattern is proven.
 *
 * Result: `draftStateMachine.advanceDraftSessionWithPoint` no longer
 * needs the `if (isDimensionStep(...))` predicate + delegation block.
 * Dispatch happens through `findInRegistry(session.step)` like every
 * other step.
 */

import { advanceDimensionWithPoint } from "../draftStateMachine.dimensions";
import type { AdvanceWithPointFn } from "../draftTypes";

/**
 * Every step recognised by `advanceDimensionWithPoint`. Must mirror
 * the predicate `isDimensionStep` in
 * `draftStateMachine.dimensions.ts` — if either drifts, the unified
 * dispatch will miss steps the legacy delegation caught.
 */
const DIMENSION_STEPS = [
  "dimension-first-point",
  "dimension-second-point",
  "dimension-line-point",
  "dimaligned-first-point",
  "dimaligned-second-point",
  "dimaligned-line-point",
  "dimangular-first-line",
  "dimangular-second-line",
  "dimangular-arc-point",
  "dim3point-vertex",
  "dim3point-first-arm",
  "dim3point-second-arm",
  "dim3point-arc-point",
  "dimradius-select-arc",
  "dimradius-dim-line",
  "dimdiameter-select-arc",
  "dimdiameter-dim-line",
  "dimordinate-feature-point",
  "dimordinate-leader-end",
  "dimbaseline-second-point",
  "dimcontinue-second-point",
] as const;

const dimWrapper: AdvanceWithPointFn = (session, point, ctx) => {
  const result = advanceDimensionWithPoint(session, point, ctx.activeSpaceId);
  // `advanceDimensionWithPoint` returns null for unknown steps. Each
  // entry below registers only the known steps so a null at the
  // wrapper site means a registry-key drift bug (caller treats null
  // as "fall through" — see `advanceWithPointFromRegistry`).
  return result;
};

export const DIMENSION_POINT_ENTRIES: ReadonlyArray<
  readonly [(typeof DIMENSION_STEPS)[number], AdvanceWithPointFn]
> = DIMENSION_STEPS.map((step) => [step, dimWrapper]);
